import React, { FC, InputHTMLAttributes, ReactNode, useEffect, useState } from "react";
import { styled } from "@mui/system";
import { styled as muiStyled } from "@mui/material/styles";
import {
  Button as MuiButton,
  ButtonProps as MuiButtonProps,
  Checkbox as MuiCheckbox,
  CircularProgress,
  IconButton as MuiIconButton,
  IconButtonProps as MuiIconButtonProps,
  MenuItem,
  Select as MuiSelect,
  Skeleton,
  Switch as MuiSwitch,
  Tooltip as MuiTooltip,
  TooltipProps as MuiTooltipProps,
} from "@mui/material";

/**
 * Outreach's own settings-page vocabulary, for the dashboard pages that also
 * live inside Outreach. The values are the ones measured off Outreach and
 * already used by pages/Outreach/LinkedInConversations.tsx: Roboto Flex 14px
 * in #454262, muted #686783, brand #5E5EAF, #E8EAF1 rules and soft fills,
 * 36px pill buttons, 8px corners.
 *
 * Every colour is a custom property set on <OuPage>, so the same parts follow
 * the dashboard's dark theme (html[data-theme="dark"]) without a second set
 * of components. Each var() carries the light value as its fallback, so a
 * part rendered outside a page (a portal) still reads as Outreach.
 */

export const OUTREACH_SANS = '"Roboto Flex", Roboto, "Helvetica Neue", Arial, sans-serif';

export const OU = {
  ink: "var(--ou-ink, #454262)",
  muted: "var(--ou-muted, #686783)",
  brand: "var(--ou-brand, #5E5EAF)",
  brandHover: "var(--ou-brand-hover, #4B4B9E)",
  primaryBg: "var(--ou-primary-bg, #5E5EAF)",
  primaryBgHover: "var(--ou-primary-bg-hover, #4B4B9E)",
  soft: "var(--ou-soft, #E8EAF1)",
  softHover: "var(--ou-soft-hover, #DCDEEA)",
  rule: "var(--ou-rule, #E8EAF1)",
  surface: "var(--ou-surface, #ffffff)",
  wash: "var(--ou-wash, #F7F7FA)",
  inputBorder: "var(--ou-input-border, rgba(0, 0, 0, 0.23))",
  inputBorderHover: "var(--ou-input-border-hover, #454262)",
  disabledText: "var(--ou-disabled-text, #A3A2BD)",
  switchOn: "var(--ou-switch-on, #7871F6)",
  switchOff: "var(--ou-switch-off, #5E5D84)",
  shadow: "var(--ou-shadow, rgba(69, 66, 98, 0.16) 0 0 1px, rgba(69, 66, 98, 0.16) 0 1px 2px)",
  bone: "var(--ou-bone, rgba(69, 66, 98, 0.11))",
  tableBg: "var(--ou-table-bg, #FEFDFC)",
  rowHover: "var(--ou-row-hover, #F7F6F9)",
  divider: "var(--ou-divider, rgba(0, 0, 0, 0.12))",
};

/* ── page ── */

export const OuPage = styled("div")`
  --ou-ink: #454262;
  --ou-muted: #686783;
  --ou-brand: #5e5eaf;
  --ou-brand-hover: #4b4b9e;
  --ou-primary-bg: #5e5eaf;
  --ou-primary-bg-hover: #4b4b9e;
  --ou-soft: #e8eaf1;
  --ou-soft-hover: #dcdeea;
  --ou-rule: #e8eaf1;
  --ou-surface: #ffffff;
  --ou-wash: #f7f7fa;
  --ou-input-border: rgba(0, 0, 0, 0.23);
  --ou-input-border-hover: #454262;
  --ou-disabled-text: #a3a2bd;
  --ou-switch-on: #7871f6;
  --ou-switch-off: #5e5d84;
  --ou-shadow: rgba(69, 66, 98, 0.16) 0 0 1px, rgba(69, 66, 98, 0.16) 0 1px 2px;
  --ou-bone: rgba(69, 66, 98, 0.11);
  --ou-table-bg: #fefdfc;
  --ou-row-hover: #f7f6f9;
  --ou-divider: rgba(0, 0, 0, 0.12);
  --ou-success-bg: #e3f5ec;
  --ou-success-fg: #1c7a4b;
  --ou-warning-bg: #fff3d6;
  --ou-warning-fg: #8a5a00;
  --ou-caution-bg: #ffeadb;
  --ou-caution-fg: #b54708;
  --ou-danger-bg: #fdeceb;
  --ou-danger-fg: #b42318;
  --ou-info-bg: #ececf7;
  --ou-info-fg: #4b4b9e;
  --ou-neutral-bg: #eeeff4;
  --ou-neutral-fg: #686783;

  html[data-theme="dark"] & {
    --ou-ink: #f1f1f4;
    --ou-muted: #bdc1c6;
    --ou-brand: #adadff;
    --ou-brand-hover: #c4c4ff;
    --ou-primary-bg: #5959cf;
    --ou-primary-bg-hover: #6b63ee;
    --ou-soft: #2b3545;
    --ou-soft-hover: #344052;
    --ou-rule: #2c3644;
    --ou-surface: #212934;
    --ou-wash: #1b222b;
    --ou-input-border: #3b4757;
    --ou-input-border-hover: #adadff;
    --ou-disabled-text: #6b7686;
    --ou-shadow: rgba(0, 0, 0, 0.4) 0 0 1px, rgba(0, 0, 0, 0.3) 0 1px 3px;
    --ou-bone: rgba(255, 255, 255, 0.08);
    --ou-table-bg: #212934;
    --ou-row-hover: #27303c;
    --ou-divider: rgba(255, 255, 255, 0.12);
    --ou-success-bg: rgba(52, 199, 123, 0.16);
    --ou-success-fg: #7ee2ae;
    --ou-warning-bg: rgba(255, 193, 7, 0.14);
    --ou-warning-fg: #ffd466;
    --ou-caution-bg: rgba(255, 140, 50, 0.16);
    --ou-caution-fg: #ffb27a;
    --ou-danger-bg: rgba(240, 80, 70, 0.16);
    --ou-danger-fg: #ff9b93;
    --ou-info-bg: rgba(173, 173, 255, 0.14);
    --ou-info-fg: #c4c4ff;
    --ou-neutral-bg: #2b3545;
    --ou-neutral-fg: #bdc1c6;
  }

  display: flex;
  flex-direction: column;
  gap: 16px;
  max-width: 900px;
  font-family: ${OUTREACH_SANS};
  font-size: 14px;
  line-height: 20px;
  color: ${OU.ink};
  /* Outreach draws its text antialiased; without this Roboto Flex reads heavier. */
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;

  a {
    color: ${OU.brand};
    font-weight: 500;
    text-decoration: none;
  }

  a:hover {
    color: ${OU.brandHover};
    text-decoration: underline;
  }
`;

/* ── cards ── */

export const OuCard = styled("section")`
  background: ${OU.surface};
  border-radius: 8px;
  box-shadow: ${OU.shadow};
  padding: 24px;
  min-width: 0;

  @media (max-width: 600px) {
    padding: 20px 16px;
  }
`;

/* Title on the left with its control on the same line at the far right; the
   description runs underneath at full width (capped at 640px), so a button
   or switch never squeezes it into a narrow column on a phone. */
const HeaderRow = styled("div")`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  grid-template-areas:
    "title aside"
    "desc desc";
  column-gap: 24px;
  row-gap: 4px;
  align-items: center;

  > h2,
  > h3 {
    grid-area: title;
  }

  > p {
    grid-area: desc;
  }

  @media (max-width: 600px) {
    column-gap: 16px;
  }
`;

const HeaderAside = styled("div")`
  grid-area: aside;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
`;

export const OuTitle = styled("h2")`
  margin: 0;
  font: 600 20px/28px ${OUTREACH_SANS};
  color: ${OU.ink};
  letter-spacing: 0;
`;

export const OuSectionTitle = styled("h3")`
  margin: 0;
  font: 600 16px/24px ${OUTREACH_SANS};
  color: ${OU.ink};
  letter-spacing: 0;
`;

export const OuDescription = styled("p")`
  margin: 0;
  font: 400 14px/20px ${OUTREACH_SANS};
  color: ${OU.muted};
  max-width: 640px;
`;

export const OuCardHeader: FC<{
  title: ReactNode;
  description?: ReactNode;
  aside?: ReactNode;
  /** "page" for the page's own title, "section" for a card within it. */
  level?: "page" | "section";
}> = ({ title, description, aside, level = "section" }) => (
  <HeaderRow>
    {level === "page" ? <OuTitle>{title}</OuTitle> : <OuSectionTitle>{title}</OuSectionTitle>}
    {aside && <HeaderAside>{aside}</HeaderAside>}
    {description && <OuDescription>{description}</OuDescription>}
  </HeaderRow>
);

/* ── setting rows: label and helper on the left, the control on the right ── */

const RowWrap = styled("div")`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  padding: 16px 0;
  border-top: 1px solid ${OU.rule};

  @media (max-width: 600px) {
    flex-wrap: wrap;
    gap: 12px 16px;

    /* A switch stays on the label's line at the right; a wide control
       (a select) drops below the label and takes the full width. */
    > div:first-of-type {
      flex: 1 1 200px;
    }

    > div:last-of-type {
      flex: 1 0 auto;
    }
  }
`;

const RowText = styled("div")`
  flex: 1 1 280px;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const RowLabel = styled("label")`
  margin: 0;
  font: 500 14px/20px ${OUTREACH_SANS};
  color: ${OU.ink};
`;

const RowHelper = styled("span")`
  font: 400 13px/18px ${OUTREACH_SANS};
  color: ${OU.muted};
  max-width: 520px;
`;

const RowControl = styled("div")`
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
`;

export const OuSettingRow: FC<{ label: ReactNode; helper?: ReactNode; htmlFor?: string; children: ReactNode }> = ({ label, helper, htmlFor, children }) => (
  <RowWrap>
    <RowText>
      <RowLabel htmlFor={htmlFor}>{label}</RowLabel>
      {helper && <RowHelper>{helper}</RowHelper>}
    </RowText>
    <RowControl>{children}</RowControl>
  </RowWrap>
);

/* ── fields ── */

/** A labelled control; the label sits above, as on Outreach's settings forms. */
export const OuFieldLabel = styled("label")`
  display: block;
  margin: 0 0 6px;
  font: 500 13px/16px ${OUTREACH_SANS};
  color: ${OU.muted};
`;

/* Outreach's outlined input: 40px, 8px corners, 23% ink outline that goes
   brand on focus. Applied to any MUI outlined input or select inside it. */
export const OuControl = styled("div")`
  min-width: 0;

  .MuiFormControl-root,
  .MuiTextField-root {
    width: 100%;
    margin: 0;
  }

  .MuiOutlinedInput-root {
    height: 40px;
    border-radius: 8px;
    background: ${OU.surface};
    font: 400 14px/20px ${OUTREACH_SANS};
    color: ${OU.ink};
  }

  .MuiOutlinedInput-input {
    box-sizing: border-box;
    height: 40px;
    padding: 10px 12px;
    font: 400 14px/20px ${OUTREACH_SANS};
    color: ${OU.ink};
  }

  .MuiOutlinedInput-input::placeholder {
    color: ${OU.muted};
    opacity: 0.8;
  }

  input[type="number"] {
    font-variant-numeric: tabular-nums;
  }

  .MuiSelect-select.MuiOutlinedInput-input {
    display: flex;
    align-items: center;
    padding-right: 36px;
    min-height: 0;
  }

  .MuiSelect-icon {
    color: ${OU.muted};
    right: 8px;
  }

  .MuiOutlinedInput-notchedOutline {
    border: 1px solid ${OU.inputBorder};
    border-radius: 8px;
  }

  .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline {
    border-color: ${OU.inputBorderHover};
  }

  .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline {
    border: 2px solid ${OU.brand};
  }

  .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline {
    border-color: ${OU.rule};
  }
`;

export const OuField: FC<{ label: ReactNode; htmlFor?: string; children: ReactNode; className?: string }> = ({ label, htmlFor, children, className }) => (
  <OuControl className={className}>
    <OuFieldLabel htmlFor={htmlFor}>{label}</OuFieldLabel>
    {children}
  </OuControl>
);

export interface OuSelectOption {
  value: string;
  label: ReactNode;
}

const ChevronDown: FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" width="20" height="20" fill="currentColor" aria-hidden="true">
    <path d="M7.41 8.59 12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41Z" />
  </svg>
);

/** Outreach's select; wrap it in <OuControl> or <OuField> for its outline. */
export const OuSelect: FC<{
  value: string;
  onChange: (value: string) => void;
  options: OuSelectOption[];
  id?: string;
  disabled?: boolean;
  "aria-label"?: string;
}> = ({ value, onChange, options, id, disabled, "aria-label": ariaLabel }) => (
  <MuiSelect
    size="small"
    fullWidth
    id={id}
    value={value}
    disabled={disabled}
    onChange={(e) => onChange(String(e.target.value))}
    IconComponent={ChevronDown}
    inputProps={{ "aria-label": ariaLabel }}
    MenuProps={{
      PaperProps: {
        sx: {
          mt: 0.5,
          borderRadius: "8px",
          boxShadow: "0 4px 16px rgba(69, 66, 98, 0.16), 0 0 1px rgba(69, 66, 98, 0.24)",
          "& .MuiMenuItem-root": {
            font: `400 14px/20px ${OUTREACH_SANS}`,
            color: "var(--color-foregroundPrimary, #454262)",
            minHeight: 40,
            px: 2,
          },
          "& .MuiMenuItem-root.Mui-selected": {
            backgroundColor: "rgba(94, 94, 175, 0.08)",
            fontWeight: 500,
          },
          "& .MuiMenuItem-root.Mui-selected:hover, & .MuiMenuItem-root:hover": {
            backgroundColor: "rgba(94, 94, 175, 0.12)",
          },
        },
      },
    }}
  >
    {options.map((o) => (
      <MenuItem key={o.value} value={o.value}>
        {o.label}
      </MenuItem>
    ))}
  </MuiSelect>
);

/* ── switch ── */

const SwitchWrap = styled("span")`
  display: inline-flex;
  flex: 0 0 auto;

  .MuiSwitch-root {
    width: 48px;
    height: 24px;
    padding: 0;
    overflow: visible;
  }

  .MuiSwitch-switchBase {
    padding: 4px;
    color: #fff;
    transition: transform 180ms cubic-bezier(0.4, 0, 0.2, 1);

    &:hover {
      background: transparent;
    }

    &.Mui-checked {
      transform: translateX(24px);
      color: #fff;
    }

    &.Mui-checked + .MuiSwitch-track {
      background: ${OU.switchOn};
      opacity: 1;
    }

    &.Mui-disabled + .MuiSwitch-track {
      opacity: 0.4;
    }

    &.Mui-focusVisible + .MuiSwitch-track {
      box-shadow: 0 0 0 3px rgba(120, 113, 246, 0.35);
    }
  }

  .MuiSwitch-input {
    left: -100%;
    width: 300%;
  }

  .MuiSwitch-track {
    border-radius: 12px;
    background: ${OU.switchOff};
    opacity: 1;
    transition: background-color 150ms ease, box-shadow 150ms ease;
  }

  .MuiSwitch-root:hover .MuiSwitch-switchBase:not(.Mui-disabled) + .MuiSwitch-track {
    box-shadow: 0 0 0 3px rgba(94, 93, 132, 0.15);
  }

  .MuiSwitch-root:hover .MuiSwitch-switchBase.Mui-checked:not(.Mui-disabled) + .MuiSwitch-track {
    box-shadow: 0 0 0 3px rgba(120, 113, 246, 0.18);
  }

  /* Saving, as the Dial faster extension draws it: the whole switch goes
     #DBDAE3, track and knob alike, the knob keeping its check or cross in the
     same grey, and it takes no clicks until the save lands. No spinner. */
  &[data-saving="true"] {
    cursor: not-allowed;

    .MuiSwitch-root {
      pointer-events: none;
    }

    .MuiSwitch-switchBase + .MuiSwitch-track,
    .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track {
      background: #dbdae3;
      opacity: 1;
      box-shadow: none;
    }
  }
`;

const Knob = styled("span", { shouldForwardProp: (prop) => prop !== "on" && prop !== "saving" })<{ on?: boolean; saving?: boolean }>`
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: ${({ saving }) => (saving ? "#DBDAE3" : "#fff")};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ on, saving }) => (saving ? "#DBDAE3" : on ? "var(--ou-switch-on, #7871F6)" : "var(--ou-switch-off, #5E5D84)")};

  svg {
    width: 12px;
    height: 12px;
  }
`;

const KnobIcon: FC<{ on: boolean; busy: boolean }> = ({ on, busy }) => (
  <Knob on={on} saving={busy}>
    {on ? (
      <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M20 6 9 17l-5-5" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ) : (
      <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M18 6 6 18M6 6l12 12" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
      </svg>
    )}
  </Knob>
);

/** Outreach's switch: no On/Off words, the knob carries a check or a cross. */
export const OuSwitch: FC<{
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
  saving?: boolean;
  id?: string;
  "aria-label"?: string;
}> = ({ checked, onChange, disabled, saving, id, "aria-label": ariaLabel }) => (
  <SwitchWrap data-saving={saving ? "true" : undefined}>
    <MuiSwitch
      id={id}
      checked={checked}
      disabled={disabled}
      disableRipple
      icon={<KnobIcon on={false} busy={!!saving} />}
      checkedIcon={<KnobIcon on busy={!!saving} />}
      inputProps={{ role: "switch", "aria-label": ariaLabel, "aria-busy": saving || undefined }}
      onChange={(e) => {
        if (saving) return;
        onChange(e.target.checked);
      }}
    />
  </SwitchWrap>
);

/* ── buttons ── */

const pill = `
  height: 36px;
  min-width: 64px;
  padding: 0 16px;
  border-radius: 100px;
  font: 600 14px/1 ${OUTREACH_SANS};
  letter-spacing: 0;
  text-transform: none;
  box-shadow: none;
  gap: 8px;
  white-space: nowrap;

  &:hover {
    box-shadow: none;
  }

  &.Mui-focusVisible {
    box-shadow: 0 0 0 3px rgba(94, 94, 175, 0.35);
  }

  &.Mui-disabled {
    background: ${OU.soft};
    color: ${OU.disabledText};
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

const PrimaryBase = muiStyled(MuiButton)`
  ${pill}
  background: ${OU.primaryBg};
  color: #fff;

  &:hover {
    background: ${OU.primaryBgHover};
  }
`;

const SecondaryBase = muiStyled(MuiButton)`
  ${pill}
  background: ${OU.soft};
  color: ${OU.brand};

  &:hover {
    background: ${OU.softHover};
  }
`;

type OuButtonProps = Omit<MuiButtonProps, "variant" | "color"> & { loading?: boolean; icon?: ReactNode };

const withLoading = (Base: typeof PrimaryBase): FC<OuButtonProps> => {
  const OuButton: FC<OuButtonProps> = ({ loading, icon, children, disabled, ...rest }) => (
    <Base disableElevation disabled={disabled || loading} aria-busy={loading || undefined} {...rest}>
      {loading ? <CircularProgress size={16} thickness={5} sx={{ color: "inherit" }} /> : icon}
      {children}
    </Base>
  );
  return OuButton;
};

export const OuPrimaryButton = withLoading(PrimaryBase);
export const OuSecondaryButton = withLoading(SecondaryBase);

/** Right-aligned button row at the bottom of a card; a status line may sit on its left. */
const FooterWrap = styled("div")`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 16px;
  margin: 8px -24px -24px;
  padding: 16px 24px;
  border-top: 1px solid ${OU.rule};

  @media (max-width: 600px) {
    margin: 4px -16px -20px;
    padding: 16px;
    flex-wrap: wrap;
  }
`;

const FooterStatus = styled("div")`
  flex: 1 1 auto;
  min-width: 0;
  font: 400 13px/18px ${OUTREACH_SANS};
  color: ${OU.muted};
`;

export const OuActions = styled("div")`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  flex: 0 0 auto;
  margin-left: auto;
`;

export const OuCardFooter: FC<{ status?: ReactNode; children: ReactNode }> = ({ status, children }) => (
  <FooterWrap>
    <FooterStatus role="status" aria-live="polite">
      {status}
    </FooterStatus>
    <OuActions>{children}</OuActions>
  </FooterWrap>
);

/* ── notices, pills, empty states ── */

export type OuTone = "success" | "warning" | "caution" | "danger" | "info" | "neutral";

export const OuPill = styled("span")<{ tone: OuTone }>`
  display: inline-block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  vertical-align: middle;
  height: 24px;
  padding: 0 10px;
  border-radius: 100px;
  font: 500 12px/24px ${OUTREACH_SANS};
  white-space: nowrap;
  background: ${({ tone }) => `var(--ou-${tone}-bg)`};
  color: ${({ tone }) => `var(--ou-${tone}-fg)`};

  &::first-letter {
    text-transform: uppercase;
  }
`;

const NoticeWrap = styled("div")<{ tone: OuTone }>`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-top: 16px;
  padding: 12px 16px;
  border-radius: 8px;
  background: ${({ tone }) => `var(--ou-${tone}-bg)`};
  color: ${OU.ink};
  font: 400 14px/20px ${OUTREACH_SANS};

  svg {
    flex: 0 0 auto;
    width: 20px;
    height: 20px;
    color: ${({ tone }) => `var(--ou-${tone}-fg)`};
  }
`;

export const OuNotice: FC<{ tone?: OuTone; children: ReactNode }> = ({ tone = "info", children }) => (
  <NoticeWrap tone={tone} role="note">
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20Zm1 15h-2v-6h2v6Zm0-8h-2V7h2v2Z" />
    </svg>
    <span>{children}</span>
  </NoticeWrap>
);

const EmptyWrap = styled("div")`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  gap: 4px;
  padding: 40px 16px 32px;
`;

const EmptyIcon = styled("div")`
  width: 48px;
  height: 48px;
  margin-bottom: 12px;
  border-radius: 50%;
  background: ${OU.wash};
  color: ${OU.muted};
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 24px;
    height: 24px;
    fill: currentColor;
  }
`;

const EmptyTitle = styled("div")`
  font: 600 14px/20px ${OUTREACH_SANS};
  color: ${OU.ink};
`;

const EmptyHint = styled("div")`
  font: 400 14px/20px ${OUTREACH_SANS};
  color: ${OU.muted};
  max-width: 420px;
`;

export const OuEmptyState: FC<{ icon: ReactNode; title: ReactNode; hint?: ReactNode }> = ({ icon, title, hint }) => (
  <EmptyWrap>
    <EmptyIcon>{icon}</EmptyIcon>
    <EmptyTitle>{title}</EmptyTitle>
    {hint && <EmptyHint>{hint}</EmptyHint>}
  </EmptyWrap>
);

/* ── skeleton ── */

/** Outreach's loading ink: MUI skeleton at 11% ink, following the theme. */
export const OuBone = styled(Skeleton)`
  background-color: ${OU.bone};
  flex: 0 0 auto;
`;

/* ── icons (Material paths, as Outreach uses) ── */

export const RefreshIcon: FC = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M17.65 6.35A7.96 7.96 0 0 0 12 4a8 8 0 1 0 7.73 10h-2.08A6 6 0 1 1 12 6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35Z" />
  </svg>
);

export const SyncIcon: FC = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M12 4V1L8 5l4 4V6c3.31 0 6 2.69 6 6 0 1.01-.25 1.97-.7 2.8l1.46 1.46A7.93 7.93 0 0 0 20 12c0-4.42-3.58-8-8-8Zm0 14c-3.31 0-6-2.69-6-6 0-1.01.25-1.97.7-2.8L5.24 7.74A7.93 7.93 0 0 0 4 12c0 4.42 3.58 8 8 8v3l4-4-4-4v3Z" />
  </svg>
);

export const QueueIcon: FC = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M4 6h16v2H4V6Zm0 5h16v2H4v-2Zm0 5h10v2H4v-2Z" />
  </svg>
);

export const PhoneIcon: FC = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M6.62 10.79a15.05 15.05 0 0 0 6.59 6.59l2.2-2.2a1 1 0 0 1 1.02-.24c1.12.37 2.33.57 3.57.57a1 1 0 0 1 1 1V20a1 1 0 0 1-1 1A17 17 0 0 1 3 4a1 1 0 0 1 1-1h3.5a1 1 0 0 1 1 1c0 1.25.2 2.45.57 3.57a1 1 0 0 1-.25 1.02l-2.2 2.2Z" />
  </svg>
);

export const ExternalIcon: FC = () => (
  <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor" aria-hidden="true" style={{ verticalAlign: "-2px", marginLeft: 4 }}>
    <path d="M14 3v2h3.59l-9.83 9.83 1.41 1.41L19 6.41V10h2V3h-7Zm5 16H5V5h7V3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7h-2v7Z" />
  </svg>
);

/* ── list pages: search, icon buttons, checkbox, table ── */

const SearchWrap = styled("label")`
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
  max-width: 360px;
  min-width: 200px;
  height: 36px;
  box-sizing: border-box;
  padding: 0 12px;
  gap: 8px;
  border: 1px solid ${OU.inputBorder};
  border-radius: 8px;
  background: ${OU.surface};
  color: ${OU.ink};
  cursor: text;

  &:hover {
    border-color: ${OU.inputBorderHover};
  }

  &:focus-within {
    border-color: ${OU.brand};
    box-shadow: inset 0 0 0 1px ${OU.brand};
  }

  svg {
    flex: 0 0 auto;
    width: 20px;
    height: 20px;
    fill: currentColor;
  }

  input {
    flex: 1 1 auto;
    min-width: 0;
    height: 100%;
    padding: 0;
    border: none;
    outline: none;
    background: transparent;
    font: 400 14px/20px ${OUTREACH_SANS};
    color: ${OU.ink};
  }

  input::placeholder {
    color: ${OU.muted};
    opacity: 1;
  }
`;

/** Outreach's list search: 36px, white, 8px corners, a magnifier on the left. */
export const OuSearch: FC<Omit<InputHTMLAttributes<HTMLInputElement>, "onChange" | "value"> & { value: string; onChange: (value: string) => void }> = ({
  value,
  onChange,
  placeholder = "Search",
  ...rest
}) => (
  <SearchWrap>
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0 0 16 9.5 6.5 6.5 0 1 0 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5Zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14Z" />
    </svg>
    <input type="search" value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} {...rest} />
  </SearchWrap>
);

const IconButtonBase = muiStyled(MuiIconButton)`
  width: 36px;
  height: 36px;
  min-width: 36px;
  padding: 0 8px;
  border-radius: 100px;
  background: ${OU.soft};
  color: ${OU.brand};
  gap: 4px;
  font: 600 13px/1 ${OUTREACH_SANS};

  &:hover {
    background: ${OU.softHover};
  }

  &.Mui-focusVisible {
    box-shadow: 0 0 0 3px rgba(94, 94, 175, 0.35);
  }

  &.Mui-disabled {
    background: ${OU.soft};
    color: ${OU.disabledText};
  }

  svg {
    width: 20px;
    height: 20px;
  }

  /* A count next to the icon turns the circle into a pill. */
  &.with-label {
    width: auto;
    padding: 0 12px 0 10px;
  }
`;

/** Outreach's round 36px action button on a soft fill (export, refresh, columns). */
export const OuIconButton: FC<MuiIconButtonProps & { label?: ReactNode }> = ({ label, children, className, ...rest }) => (
  <IconButtonBase disableRipple className={[className, label != null ? "with-label" : ""].filter(Boolean).join(" ")} {...rest}>
    {children}
    {label}
  </IconButtonBase>
);

/** Outreach's plain 18px square checkbox in a round 34px hover target. */
export const OuCheckbox: FC<{ checked: boolean; indeterminate?: boolean; onChange: (checked: boolean) => void; "aria-label"?: string }> = ({
  checked,
  indeterminate,
  onChange,
  "aria-label": ariaLabel,
}) => (
  <MuiCheckbox
    checked={checked}
    indeterminate={indeterminate}
    onChange={(e) => onChange(e.target.checked)}
    onClick={(e) => e.stopPropagation()}
    disableRipple
    inputProps={{ "aria-label": ariaLabel }}
    sx={{
      padding: "8px",
      borderRadius: "50%",
      color: "var(--ou-muted, #686783)",
      "& .MuiSvgIcon-root": { fontSize: 24 },
      "&:hover": { backgroundColor: "rgba(94, 94, 175, 0.08)" },
      "&.Mui-checked, &.MuiCheckbox-indeterminate": { color: "var(--ou-brand, #5E5EAF)" },
      "&.Mui-focusVisible": { boxShadow: "0 0 0 3px rgba(94, 94, 175, 0.35)" },
    }}
  />
);

/** "6,618 results", top right of an Outreach list. */
export const OuResultCount = styled("span")`
  font: 400 14px/20px ${OUTREACH_SANS};
  color: ${OU.muted};
  white-space: nowrap;
`;

export interface OuTableColumn<T> {
  key: string;
  header: ReactNode;
  /** Pixels; the table is laid out fixed and scrolls sideways past its container. */
  width: number;
  render: (row: T) => ReactNode;
  /** Stays put on horizontal scroll, with Outreach's hairline after it. Only the first column should set it. */
  sticky?: boolean;
  align?: "left" | "center";
}

const CHECK_COL = 52;

const TableScroll = styled("div")`
  width: 100%;
  overflow-x: auto;
  background: ${OU.tableBg};
  font-family: ${OUTREACH_SANS};
  -webkit-overflow-scrolling: touch;

  table {
    table-layout: fixed;
    border-collapse: separate;
    border-spacing: 0;
    min-width: 100%;
  }

  th,
  td {
    box-sizing: border-box;
    background: ${OU.tableBg};
    border-bottom: 0.5px solid ${OU.rule};
    text-align: left;
    vertical-align: middle;
  }

  th {
    height: 48px;
    padding: 4px 12px;
    font: 600 13px/16px ${OUTREACH_SANS};
    color: ${OU.muted};
    white-space: normal;
    hyphens: auto;
    overflow-wrap: break-word;
  }

  td {
    height: 64px;
    padding: 8px 12px;
    font: 400 14px/20px ${OUTREACH_SANS};
    color: ${OU.ink};
  }

  tbody tr:hover td {
    background: ${OU.rowHover};
  }

  .ou-check {
    padding: 0 0 0 6px;
  }

  .ou-sticky {
    position: sticky;
    z-index: 1;
  }

  .ou-sticky-last {
    border-right: 0.5px solid ${OU.divider};
  }

  .ou-center {
    text-align: center;
  }

  /* Values sit on one line and end in an ellipsis, as Outreach's do. */
  .ou-cell {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  /* Row actions (copy and the like) only show on the row under the pointer. */
  .ou-row-action {
    opacity: 0;
    transition: opacity 120ms ease;
  }

  tr:hover .ou-row-action,
  .ou-row-action:focus-visible {
    opacity: 1;
  }
`;

const MoreRow = styled("div")`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 16px 0 8px;
  font: 400 14px/20px ${OUTREACH_SANS};
  color: ${OU.muted};
`;

export interface OuTableProps<T> {
  columns: OuTableColumn<T>[];
  rows: T[];
  getRowId: (row: T) => string;
  /** With a selection, the first column is Outreach's checkbox and the header one selects every row. */
  selection?: { selected: Set<string>; onChange: (next: Set<string>) => void };
  loading?: boolean;
  skeletonRows?: number;
  /** Rows drawn at first; "Show more" adds this many again. Outreach scrolls a long list rather than paging it. */
  pageSize?: number;
}

/**
 * Outreach's prospects table: a 48px header of 13px muted labels that wrap,
 * 64px rows on the page surface split by hairlines, no column rules except
 * the one after the sticky first column, plain 14px values with ellipsis.
 */
export function OuTable<T>({ columns, rows, getRowId, selection, loading, skeletonRows = 8, pageSize = 100 }: OuTableProps<T>): JSX.Element {
  const [visible, setVisible] = useState(pageSize);
  useEffect(() => setVisible(pageSize), [rows, pageSize]);

  const shown = rows.slice(0, visible);
  const ids = rows.map(getRowId);
  const allSelected = !!selection && ids.length > 0 && ids.every((id) => selection.selected.has(id));
  const someSelected = !!selection && ids.some((id) => selection.selected.has(id));
  const offset = selection ? CHECK_COL : 0;
  const width = offset + columns.reduce((sum, c) => sum + c.width, 0);

  const stickyStyle = (c: OuTableColumn<T>) => (c.sticky ? { left: offset } : undefined);
  const cellClass = (c: OuTableColumn<T>) => [c.sticky ? "ou-sticky ou-sticky-last" : "", c.align === "center" ? "ou-center" : ""].filter(Boolean).join(" ") || undefined;

  return (
    <>
      <TableScroll>
        <table style={{ width }}>
          <colgroup>
            {selection && <col style={{ width: CHECK_COL }} />}
            {columns.map((c) => (
              <col key={c.key} style={{ width: c.width }} />
            ))}
          </colgroup>
          <thead>
            <tr>
              {selection && (
                <th className="ou-check ou-sticky" style={{ left: 0 }}>
                  <OuCheckbox
                    checked={allSelected}
                    indeterminate={someSelected && !allSelected}
                    aria-label="Select all"
                    onChange={(on) => selection.onChange(on ? new Set(ids) : new Set())}
                  />
                </th>
              )}
              {columns.map((c) => (
                <th key={c.key} scope="col" className={cellClass(c)} style={stickyStyle(c)}>
                  {c.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading
              ? Array.from({ length: skeletonRows }, (_, i) => (
                  <tr key={`bone-${i}`} aria-hidden="true">
                    {selection && (
                      <td className="ou-check ou-sticky" style={{ left: 0 }}>
                        <OuBone variant="rounded" width={18} height={18} style={{ marginLeft: 8 }} />
                      </td>
                    )}
                    {columns.map((c, j) => (
                      <td key={c.key} className={cellClass(c)} style={stickyStyle(c)}>
                        <OuBone variant="text" width={`${[70, 55, 80, 45, 65][(i + j) % 5]}%`} style={{ fontSize: 14 }} />
                        {c.sticky && <OuBone variant="text" width="45%" style={{ fontSize: 14 }} />}
                      </td>
                    ))}
                  </tr>
                ))
              : shown.map((row) => {
                  const id = getRowId(row);
                  return (
                    <tr key={id}>
                      {selection && (
                        <td className="ou-check ou-sticky" style={{ left: 0 }}>
                          <OuCheckbox
                            checked={selection.selected.has(id)}
                            aria-label="Select row"
                            onChange={(on) => {
                              const next = new Set(selection.selected);
                              if (on) next.add(id);
                              else next.delete(id);
                              selection.onChange(next);
                            }}
                          />
                        </td>
                      )}
                      {columns.map((c) => (
                        <td key={c.key} className={cellClass(c)} style={stickyStyle(c)}>
                          {c.render(row)}
                        </td>
                      ))}
                    </tr>
                  );
                })}
          </tbody>
        </table>
      </TableScroll>
      {!loading && rows.length > visible && (
        <MoreRow>
          <span>
            Showing {visible.toLocaleString()} of {rows.length.toLocaleString()}
          </span>
          <OuSecondaryButton onClick={() => setVisible(visible + pageSize)}>Show more</OuSecondaryButton>
        </MoreRow>
      )}
    </>
  );
}

/* ── tooltip ── */

const TIP_BG = "#4d4b6c";

/**
 * Outreach's tooltip: a small rounded dark label with an arrow. The classes
 * hook into the index.css rules that beat the dashboard's global Tooltip rule,
 * which otherwise paints the popper wrapper into a square box.
 */
export const OuTooltip: FC<MuiTooltipProps> = ({ children, ...rest }) => (
  <MuiTooltip
    arrow
    enterDelay={300}
    {...rest}
    slotProps={{
      tooltip: {
        className: "boring-outreach-tip",
        sx: { background: TIP_BG, color: "#fff", font: `500 14px/20px ${OUTREACH_SANS}`, padding: "8px 16px", borderRadius: "6px", boxShadow: "none", maxWidth: 280 },
      },
      arrow: { sx: { color: TIP_BG } },
      popper: { className: "boring-outreach-popper", modifiers: [{ name: "offset", options: { offset: [0, 4] } }] },
    }}
  >
    {children}
  </MuiTooltip>
);
